import { debounce } from "./utils.js";
import { computePosition, autoUpdate } from "@floating-ui/dom";

import gsap from "gsap";
import Draggable from "gsap/Draggable";
import InertiaPlugin from "gsap/InertiaPlugin";

gsap.registerPlugin(Draggable, InertiaPlugin);

/**
 * 🎚️ Base RangeSlider Class
 *
 * Creates an interactive range slider with:
 * 🔘 Draggable thumb
 * 🌈 Color interpolation for thermometer-style sliders
 * 🔢 Customizable min/max values
 * 🏷️ Value label display
 * 📏 Responsive to window resizing
 *
 */
class RangeSlider {
  /**
   * 🏗️ Creates an instance of RangeSlider.
   * @param {HTMLElement} element - The container element for the range slider.
   * @param {Object} options - Configuration options for the slider.
   * @param {string[]} [options.colorScale] - Custom color scale for thermometer style.
   * @param {Function} [options.formatLabel] - Custom function to format the label.
   */
  constructor(element, options = {}) {
    if (!element) throw new Error("Element is required");
    this.element = element;
    this.rangeSlider = element.querySelector(
      `.${this.orientation}-range-slider`
    );
    this.track = element.querySelector(
      `.${this.orientation}-range-slider__track`
    );
    this.thumb = element.querySelector(
      `.${this.orientation}-range-slider__thumb`
    );
    this.input = element.querySelector(".range-slider__input");
    this.thumbLabel = this.thumb?.querySelector(
      `.${this.orientation}-range-slider__thumb-label`
    );
    this.averageElement = element.querySelector(
      `.${this.orientation}-range-slider__average`
    );

    if ((!this.track || !this.thumb || !this.input, !this.averageElement)) {
      throw new Error("Required elements are missing");
    }

    this.isThermometer = this.rangeSlider.classList.contains(
      `${this.orientation}-range-slider--thermometer`
    );

    this.hasDynamicOpacity = this.element.classList.contains('range-slider--dynamic-opacity');

    // Get the tooltip texts and convert the json string to an object
    if (this.element.dataset.tooltipTexts) {
      this.tooltipTexts = JSON.parse(this.element.dataset.tooltipTexts);
    } else {
      this.tooltipTexts = false;
    }

    this.colorScale = options.colorScale || [
      "#2BCC4A",
      "#FFF500",
      "#FF6507",
      "#F8032A",
    ];
    this.formatLabel =
      options.formatLabel || ((value) => `${Math.round(value)}%`);

    this.done = false; // Flag to track if the slider is done

    // Create the debounced function once in the constructor
    this.debouncedSave = debounce((key, value) => {
      localStorage.setItem(key, value);
    }, 100);

    window.addEventListener("load", () => {
      this.init();
    });
  }

  /**
   * Initializes the range slider
   */
  init() {
    this.minValue = Number(this.input.min) || 0;
    this.maxValue = Number(this.input.max) || 100;

    this.input.value = this.clamp(
      Number(this.input.value) || this.minValue,
      this.minValue,
      this.maxValue
    );

    this.setupDraggable();
    this.updateUI(Number(this.input.value));
    this.input.addEventListener("input", this.handleInputChange);
    this.input.addEventListener("showAverage", this.showAverage);
    window.addEventListener("resize", this.refresh);
    document.addEventListener("refreshRangeSlider", this.refresh);

    // Add event listeners for labels
    const labels = this.element.querySelectorAll(
      `.${this.orientation}-range-slider__label`
    );
    labels.forEach((label) => {
      label.addEventListener("click", this.handleLabelClick);
    });

    this.track.addEventListener("click", this.handleTrackClick);

    gsap.to(this.rangeSlider, {
      autoAlpha: 1,
      duration: 0.25,
      ease: "power1.inOut",
      onComplete: () => {
        if (
          this.rangeSlider ===
            document.querySelector(`.${this.orientation}-range-slider`) &&
          gsap.getProperty(this.rangeSlider, "display") !== "none"
        ) {
          this.animateInitialDrag();
        }
      },
    });
  }

  /**
   * Animates the initial drag of the thumb
   */
  animateInitialDrag() {
    const initialValue = Number(this.input.value);

    if (initialValue !== 50) return;

    this.initialDragAnimation = gsap
      .timeline({
        defaults: {
          duration: 1,
          ease: "power3.inOut",
        },
        onUpdate: () => {
          const currentPos = gsap.getProperty(this.thumb, this.axis);
          const currentValue = this.getValueFromPosition(currentPos);
          this.updateUI(currentValue);
          this.input.value = Math.round(currentValue);
        },
        onComplete: () => {
          this.updateUI(initialValue);
          this.input.value = Math.round(initialValue);
        },
      })
      .to(this.thumb, {
        [this.axis]: this.getPositionFromValue(initialValue + 20),
      })
      .to(this.thumb, {
        [this.axis]: this.getPositionFromValue(initialValue),
      });
  }

  handleTrackClick = (event) => {
    if (this.done) return; // Don't handle clicks if done

    const trackRect = this.track.getBoundingClientRect();
    const clickPosition =
      this.orientation === "vertical"
        ? trackRect.bottom - event.clientY
        : event.clientX - trackRect.left;

    const trackSize =
      this.orientation === "vertical" ? trackRect.height : trackRect.width;

    const clickProgress = this.clamp(clickPosition / trackSize, 0, 1);
    const newValue = this.progressToValue(clickProgress);

    this.initialDragAnimation?.kill();

    gsap.to(this.input, {
      value: newValue,
      ease: "power3.inOut",
      duration: 0.5,
      onUpdate: () => {
        this.updateUI(Number(this.input.value));
      },
    });
  };

  handleLabelClick = (event) => {
    if (this.done) return; // Don't handle clicks if done

    const label = event.target;
    let value;

    if (
      label.classList.contains(`${this.orientation}-range-slider__label--min`)
    ) {
      value = this.minValue;
    } else if (
      label.classList.contains(
        `${this.orientation}-range-slider__label--center`
      )
    ) {
      value = (this.minValue + this.maxValue) / 2;
    } else if (
      label.classList.contains(`${this.orientation}-range-slider__label--max`)
    ) {
      value = this.maxValue;
    } else {
      return; // Not a label we're interested in
    }

    this.initialDragAnimation?.kill();

    gsap.to(this.input, {
      value: value,
      ease: "power3.inOut",
      duration: 0.5,
      onUpdate: () => {
        this.updateUI(Number(this.input.value));
      },
    });
  };

  /**
   * Sets up the draggable functionality for the thumb
   */
  setupDraggable() {
    const draggableOptions = {
      type: this.axis,
      bounds: this.track,
      inertia: true,
      onDrag: this.updateFromDrag,
      onThrowUpdate: this.updateFromDrag,
      zIndexBoost: false,
    };

    this.draggable = Draggable.create(this.thumb, draggableOptions)[0];
  }

  /**
   * Updates the slider based on drag interaction
   */
  updateFromDrag = () => {
    if (this.done) return; // Don't update if done

    this.initialDragAnimation?.kill();
    const value = this.progressToValue(this.getProgress());
    this.updateUI(value);
    this.input.value = Math.round(value);
  };

  /**
   * Gets the current progress of the slider
   * @returns {number} The progress value between 0 and 1
   */
  getProgress() {
    const { [this.startEdge]: trackStart, [this.dimension]: trackSize } =
      this.track.getBoundingClientRect();
    const { [this.startEdge]: thumbStart, [this.dimension]: thumbSize } =
      this.thumb.getBoundingClientRect();
    return this.clamp(
      (thumbStart - trackStart) / (trackSize - thumbSize),
      0,
      1
    );
  }

  /**
   * Converts progress to a value
   * @param {number} progress - The progress value between 0 and 1
   * @returns {number} The corresponding value
   */
  progressToValue(progress) {
    return this.minValue + (this.maxValue - this.minValue) * progress;
  }

  /**
   * Updates the UI based on a value
   * @param {number} value - The value of the slider
   */
  updateUI(value) {
    if (this.done) return; // Don't update if done

    this.updateThumbPosition(value);
    this.updateLabel(value);
    if (this.isThermometer) {
      this.updateColor(value);
    }
    if(this.hasDynamicOpacity) {
      this.updateOpacity(value);
    }
  }

  /**
   * Updates the position of the thumb
   * @param {number} value - The current value of the slider
   */
  updateThumbPosition(value) {
    const progress = this.valueToProgress(value);
    const pos =
      progress *
      (this.track[`offset${this.Dimension}`] -
        this.thumb[`offset${this.Dimension}`]);
    gsap.set(this.thumb, { [this.axis]: pos });
  }

  /**
   * Updates the label of the thumb
   * @param {number} value - The current value of the slider
   */
  updateLabel(value) {
    if (
      this.thumbLabel?.classList.contains(
        `${this.orientation}-range-slider__thumb-label--value`
      )
    ) {
      const clampedValue = this.clamp(value, this.minValue, this.maxValue);
      this.thumbLabel.textContent = this.formatLabel(clampedValue);
    }
  }

  /**
   * Updates the color of the thermometer-style slider
   * @param {number} value - The current value of the slider
   */
  updateColor(value) {
    if (!this.isThermometer) return;
    const progress = this.valueToProgress(value);
    const color = gsap.utils.interpolate(this.colorScale, progress);
    gsap.set(document.documentElement, { "--color-primary": color });

    this.debouncedSave("colorPrimary", color);
  }

  updateOpacity(value) {
    const progress = this.valueToProgress(value);
    // Clamp opacity from 0.1 to 1 based on progress
    const opacity = this.clamp(progress, 0.1, 1);
    // Set the opacity of the range slider
    gsap.set(this.element.querySelector('.range-slider__label'), { opacity });
  }

  /**
   * Converts a value to progress
   * @param {number} value - The value to convert
   * @returns {number} The progress value between 0 and 1
   */
  valueToProgress(value) {
    return (value - this.minValue) / (this.maxValue - this.minValue);
  }

  /**
   * Clamps a value between a minimum and maximum
   * @param {number} value - The value to clamp
   * @param {number} min - The minimum allowed value
   * @param {number} max - The maximum allowed value
   * @returns {number} The clamped value
   */
  clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  /**
   * Handles changes to the input element
   */
  handleInputChange = () => {
    if (this.done) return; // Don't handle changes if done

    this.initialDragAnimation?.kill();
    const value = Number(this.input.value);
    this.updateUI(value);
  };

  /**
   * Refreshes the slider bounds and thumb position
   */
  refresh = () => {
    this.initialDragAnimation?.kill();
    this.draggable.applyBounds(this.track);
    const value = Number(this.input.value);
    this.updateThumbPosition(value);
    this.updateLabel(value);
  };

  /**
   * Creates the average tooltip element for the slider
   */
  createAverageTooltip = () => {
    if (!this.averageTooltipElement) {
      this.averageTooltipElement = document.createElement("div");
      this.averageTooltipElement.className = `range-slider__average-tooltip ${this.orientation}-range-slider__average-tooltip`;
      this.averageTooltipElement.innerHTML = `<svg width="43" height="43" viewBox="0 0 43 43" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M38.4473 0.830078L42.2178 4.60059L37.7236 9.09473C38.8662 10.5166 39.7676 12.04 40.4277 13.665C41.418 16.0771 41.9131 18.7051 41.9131 21.5488C41.9131 24.3164 41.418 26.9062 40.4277 29.3184C39.4375 31.7305 37.9395 33.9395 35.9336 35.9453C33.9277 37.9258 31.7188 39.4238 29.3066 40.4395C26.8945 41.4551 24.2793 41.9629 21.4609 41.9629C18.668 41.9629 16.0654 41.4551 13.6533 40.4395C12.0283 39.7793 10.5049 38.8779 9.08301 37.7354L4.58887 42.2295L0.818359 38.459L5.27441 34.0029C4.13184 32.5303 3.21777 30.9688 2.53223 29.3184C1.54199 26.9062 1.04688 24.3164 1.04688 21.5488C1.04688 18.7051 1.54199 16.0771 2.53223 13.665C3.52246 11.2529 5.02051 9.04395 7.02637 7.03809C9.03223 5.03223 11.2412 3.52148 13.6533 2.50586C16.0654 1.49023 18.668 0.982422 21.4609 0.982422C24.2793 0.982422 26.8945 1.49023 29.3066 2.50586C30.957 3.2168 32.5186 4.14355 33.9912 5.28613L38.4473 0.830078ZM27.25 7.49512C25.4727 6.7334 23.543 6.35254 21.4609 6.35254C19.4043 6.35254 17.4746 6.7334 15.6719 7.49512C13.9199 8.25684 12.3076 9.36133 10.835 10.8086C9.33691 12.3066 8.21973 13.9443 7.4834 15.7217C6.77246 17.499 6.41699 19.4414 6.41699 21.5488C6.41699 23.5801 6.77246 25.4844 7.4834 27.2617C7.91504 28.2773 8.46094 29.2422 9.12109 30.1562L30.1445 9.13281C29.2305 8.47266 28.2656 7.92676 27.25 7.49512ZM33.915 12.9033L12.8916 33.9268C13.7803 34.5615 14.707 35.082 15.6719 35.4883C17.4746 36.25 19.4043 36.6309 21.4609 36.6309C23.543 36.6309 25.4727 36.25 27.25 35.4883C29.0273 34.7266 30.6523 33.6221 32.125 32.1748C33.623 30.6768 34.7275 29.0391 35.4385 27.2617C36.1748 25.4844 36.543 23.5801 36.543 21.5488C36.543 19.4414 36.1875 17.499 35.4766 15.7217C35.0703 14.7314 34.5498 13.792 33.915 12.9033Z" fill="currentColor" /></svg>`;
      this.rangeSlider.appendChild(this.averageTooltipElement);

      // Initial positioning
      this.updateAverageTooltipPosition();
    }
  };

  /**
   * Creates the average speech bubble element for the slider
   * @param {string} text - The text to display in the speech bubble
   */
  createAverageSpeechBubble = (text) => {
    if (!this.averageSpeechBubbleElement) {
      this.averageSpeechBubbleElement = document.createElement("div");
      this.averageSpeechBubbleElement.className = `range-slider__average-speech-bubble ${this.orientation}-range-slider__average-speech-bubble`;
      this.averageSpeechBubbleElement.innerHTML = `<div>${text}</div>`;
      this.rangeSlider.appendChild(this.averageSpeechBubbleElement);
    }
  };

  /**
   * Updates the position of the average tooltip
   */
  updateAverageTooltipPosition = () => {
    let placement = this.element.dataset.horizontalTooltipPlacement;

    if (this.orientation === "vertical") {
      placement = "right";
    }

    computePosition(this.averageElement, this.averageTooltipElement, {
      placement: placement,
    }).then(({ x, y }) => {
      Object.assign(this.averageTooltipElement.style, {
        left: `${x}px`,
        top: `${y}px`,
      });
    });
  };

  /**
   * Shows the average value and completes the slider interaction
   * @param {CustomEvent} event - The custom event with the average value
   */
  showAverage = (event) => {
    if (this.done) return; // Prevent showing average multiple times

    let average = event.detail.average;

    // Update the average element's position
    gsap.set(this.rangeSlider, {
      "--average-offset": average + "%",
    });

    // Create a tooltip element if it doesn't exist
    if (!this.averageTooltipElement) this.createAverageTooltip();

    // Check if tooltip texts are available
    if (this.tooltipTexts) {
      const userValue = parseInt(this.input.value, 10);
      const tolerance = 10;
      let averageText = "";

      // Determine the appropriate tooltip text based on the comparison
      if (Math.abs(average - userValue) <= tolerance) {
        averageText = this.tooltipTexts.average;
      } else if (userValue < average) {
        averageText = this.tooltipTexts.belowAverage;
      } else {
        averageText = this.tooltipTexts.aboveAverage;
      }

      // Create a speech bubble element with the appropriate text
      this.createAverageSpeechBubble(averageText);

      let tl = gsap.timeline({
        delay: 0.25,
      });

      tl.to(`.${this.orientation}-range-slider__label`, {
        autoAlpha: 0,
      });

      tl.to(this.averageSpeechBubbleElement, {
        autoAlpha: 1,
      });

      tl.from(
        this.averageSpeechBubbleElement,
        {
          scale: 0,
          transformOrigin: () =>
            this.orientation === "vertical" ? "left" : "top",
          yPercent: () => (this.orientation === "vertical" ? 0 : -20),
          xPercent: () => (this.orientation === "vertical" ? -20 : 0),
          ease: "power4.out",
        },
        "<"
      );
    }

    // Set up autoUpdate for the average tooltip
    this.cleanup = autoUpdate(
      this.averageElement,
      this.averageTooltipElement,
      this.updateAverageTooltipPosition
    );

    gsap.to(this.averageTooltipElement, {
      autoAlpha: 1,
    });

    gsap.from(this.averageTooltipElement, {
      yPercent: () => (this.orientation === "vertical" ? 0 : -20),
      xPercent: () => (this.orientation === "vertical" ? 20 : 0),
      duration: 0.5,
      ease: "bounce.out",
    });

    // Mark as done and disable further interaction
    this.done = true;
    this.disableSlider();
  };

  /**
   * Disables the slider and applies a visual indication
   */
  disableSlider() {
    this.input.disabled = true;
    if (this.draggable) {
      this.draggable.disable();
    }
  }

  /**
   * Cleans up event listeners and animations
   */
  destroy() {
    this.input.removeEventListener("input", this.handleInputChange);
    this.input.removeEventListener("showAverage", this.showAverage);
    window.removeEventListener("resize", this.refresh);
    document.removeEventListener("refreshRangeSlider", this.refresh);
    if (this.draggable) {
      this.draggable.kill();
    }
    if (this.cleanup) {
      this.cleanup();
    }
    if (this.averageTooltipElement) {
      this.averageTooltipElement.remove();
    }
    // Remove event listeners for labels
    const labels = this.element.querySelectorAll(
      `.${this.orientation}-range-slider__label`
    );
    labels.forEach((label) => {
      label.removeEventListener("click", this.handleLabelClick);
    });
    this.track.removeEventListener("click", this.handleTrackClick);
  }
}

/**
 * 🎚️ VerticalRangeSlider Class
 */
class VerticalRangeSlider extends RangeSlider {
  constructor(element, options) {
    super(element, options);
  }

  get orientation() {
    return "vertical";
  }
  get axis() {
    return "y";
  }
  get startEdge() {
    return "bottom";
  }
  get dimension() {
    return "height";
  }
  get Dimension() {
    return "Height";
  }

  /**
   * Converts a value to a position on the vertical slider
   * @param {number} value - The value to convert
   * @returns {number} The position in pixels
   */
  getPositionFromValue(value) {
    const progress = this.valueToProgress(value);
    return (1 - progress) * (this.track.offsetHeight - this.thumb.offsetHeight);
  }

  /**
   * Converts a position to a value on the vertical slider
   * @param {number} y - The position in pixels
   * @returns {number} The corresponding value
   */
  getValueFromPosition(y) {
    const progress =
      1 - y / (this.track.offsetHeight - this.thumb.offsetHeight);
    return this.progressToValue(progress);
  }

  /**
   * Gets the current progress of the vertical slider
   * @returns {number} The progress value between 0 and 1
   */
  getProgress() {
    const { bottom: trackBottom, height: trackHeight } =
      this.track.getBoundingClientRect();
    const { bottom: thumbBottom, height: thumbHeight } =
      this.thumb.getBoundingClientRect();
    return this.clamp(
      (trackBottom - thumbBottom) / (trackHeight - thumbHeight),
      0,
      1
    );
  }

  /**
   * Updates the position of the thumb for the vertical slider
   * @param {number} value - The current value of the slider
   */
  updateThumbPosition(value) {
    const progress = this.valueToProgress(value);
    const pos =
      (1 - progress) * (this.track.offsetHeight - this.thumb.offsetHeight);
    gsap.set(this.thumb, { [this.axis]: pos });
  }
}

/**
 * 🎚️ HorizontalRangeSlider Class
 */
class HorizontalRangeSlider extends RangeSlider {
  constructor(element, options) {
    super(element, options);
  }

  get orientation() {
    return "horizontal";
  }
  get axis() {
    return "x";
  }
  get startEdge() {
    return "left";
  }
  get dimension() {
    return "width";
  }
  get Dimension() {
    return "Width";
  }

  /**
   * Converts a value to a position on the horizontal slider
   * @param {number} value - The value to convert
   * @returns {number} The position in pixels
   */
  getPositionFromValue(value) {
    const progress = this.valueToProgress(value);
    return progress * (this.track.offsetWidth - this.thumb.offsetWidth);
  }

  /**
   * Converts a position to a value on the horizontal slider
   * @param {number} x - The position in pixels
   * @returns {number} The corresponding value
   */
  getValueFromPosition(x) {
    const progress = x / (this.track.offsetWidth - this.thumb.offsetWidth);
    return this.progressToValue(progress);
  }
}

export { VerticalRangeSlider, HorizontalRangeSlider };
